import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useCart, writeCart, unitPrice, formatPrice } from '../utils/cart';
import { formatPhoneInput } from '../utils/phoneMask';
import { reachGoal } from '../utils/metrika';
import ConsentCheckbox from '../components/ConsentCheckbox';
import './CartPage.css';

const apiUrl = () => (window.location.port === '3000' ? 'http://localhost:5000/api/cart' : '/api/cart');

const CartPage = () => {
  const items = useCart();
  const [form, setForm] = useState({ phone: '', comment: '', website: '' });
  const [status, setStatus] = useState(null); // null | 'sending' | 'ok' | error text

  const set = (k) => (e) => setForm({ ...form, [k]: k === 'phone' ? formatPhoneInput(e.target.value) : e.target.value });
  const setQty = (id, qty) => writeCart(items.map((x) => (x.id === id ? { ...x, qty: Math.max(1, qty) } : x)));
  const remove = (id) => writeCart(items.filter((x) => x.id !== id));

  const total = items.reduce((s, x) => s + (unitPrice(x) ?? 0) * (x.qty || 1), 0);
  const approx = items.some((x) => x.price == null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, '').length !== 11) {
      setStatus('Введите телефон полностью: +7 (___) ___-__-__');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(apiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          total,
          items: items.map((x) => ({
            sku: x.sku,
            name: x.name,
            qty: x.qty || 1,
            price: unitPrice(x),
            height: x.height,
            width: x.width,
            depth: x.depth,
            note: x.note,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Не удалось отправить заявку');
      reachGoal('cart-order');
      writeCart([]);
      setStatus('ok');
    } catch (err) {
      setStatus(err.message);
    }
  };

  return (
    <>
      <Header />
      <section className="cart-page">
        <div className="container-custom">
          <h1 className="cart-title">Корзина</h1>

          {status === 'ok' && <p className="cart-done">Заявка отправлена! Менеджер свяжется с вами в ближайшее время.</p>}

          {!items.length ? (
            <div className="cart-empty">
              <p>Корзина пуста. Соберите корпус в конфигураторе и добавьте его в корзину.</p>
              <Link to="/configurator" className="cart-btn">Открыть конфигуратор</Link>
            </div>
          ) : (
            <div className="cart-layout">
              <div className="cart-list">
                {items.map((x) => {
                  const p = unitPrice(x);
                  const qty = x.qty || 1;
                  const size = [x.height, x.width, x.depth].filter(Boolean).join(' × ');
                  const img = x.views?.front;
                  return (
                    <article className="cart-item" key={x.id}>
                      {img && <img className="cart-item-img" src={img} alt="" />}
                      <div className="cart-item-body">
                        <div className="cart-item-sku">{x.sku}</div>
                        <h3 className="cart-item-name">{x.name}</h3>
                        {size && <div className="cart-item-size">{size} мм</div>}
                        {x.techLines?.length > 0 && (
                          <details className="cart-item-desc">
                            <summary>{(x.techTitle || 'Описание').replace(/:$/, '')}</summary>
                            <ul>{x.techLines.map((l, i) => <li key={i}>{l}</li>)}</ul>
                          </details>
                        )}
                        <div className="cart-item-foot">
                          <div className="cart-qty">
                            <button type="button" onClick={() => setQty(x.id, qty - 1)} disabled={qty <= 1} aria-label="Меньше">−</button>
                            <span>{qty}</span>
                            <button type="button" onClick={() => setQty(x.id, qty + 1)} aria-label="Больше">+</button>
                          </div>
                          <div className="cart-item-price">
                            {p == null ? 'по запросу' : `${x.price == null ? '≈ ' : ''}${formatPrice(p * qty)}`}
                          </div>
                          <button type="button" className="cart-remove" onClick={() => remove(x.id)}>Удалить</button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <form className="cart-form" onSubmit={submit}>
                <div className="cart-total">
                  <span>Итого</span>
                  <strong>{approx ? '≈ ' : ''}{formatPrice(total)}</strong>
                </div>
                <p className="cart-note">Цена ориентировочная, итог подтвердит менеджер. Без онлайн-оплаты.</p>
                <input placeholder="+7 (___) ___-__-__ *" required type="tel" inputMode="tel" maxLength={18} value={form.phone} onChange={set('phone')} autoComplete="tel" />
                <textarea placeholder="Комментарий" rows={3} value={form.comment} onChange={set('comment')} />
                <input className="cart-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set('website')} />
                <ConsentCheckbox />
                {status && status !== 'ok' && status !== 'sending' && <p className="cart-error">{status}</p>}
                <button className="cart-btn" type="submit" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Отправка...' : 'Отправить заявку'}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CartPage;
